import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { CacheKeyService } from '../cache-key.service';
import { DBService } from '../db.service';
import apiPaths from 'constants/api-paths';
import { appDb } from 'dexie/app.db';
import { generateURL } from 'helpers/api-path.helper';
import { switchMap, from, map, catchError, throwError, of } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class MissionsApiService {
  protected dbService = inject(DBService);
  protected cacheKeyService = inject(CacheKeyService);
  protected http = inject(HttpClient);
  constructor() {}
  getMissions(cacheKey?: string) {
    return this.http
      .get(
        generateURL(
          apiPaths.missionList,
          {},
          new HttpParams({ fromObject: { cacheKey } })
        )
      )
      .pipe(
        switchMap((data: any) => {
          if (data.isAllDb) {
            return from(
              appDb.missions.clear().then(() =>
                appDb.missions.bulkAdd(
                  data.items.map((item) => ({
                    ...item,
                    isSynced: 1,
                    isDeleted: item.isDeleted ? 1 : 0,
                  }))
                )
              )
            );
          }
          return from(
            appDb.missions.bulkPut(
              data.items.map((item) => ({
                ...item,
                isSynced: 1,
                isDeleted: item.isDeleted ? 1 : 0,
              }))
            )
          );
        }),
        catchError(() => {
          return of(null);
        })
      );
  }

  create(mission) {
    return this.http.post(generateURL(apiPaths.mission), mission).pipe(
      switchMap((res) =>
        this.dbService.getRow(appDb.cacheKeys, 'userMissionKey').pipe(
          switchMap((cache) => {
            return this.getMissions(cache.hash).pipe(map(() => res));
          })
        )
      ),
      switchMap((res) =>
        this.cacheKeyService
          .checkAndUpdateTables('userMissionKey')
          .pipe(map(() => res))
      ),
      catchError((err) => throwError(() => err))
    );
  }

  update(id, data) {
    return this.http.put(generateURL(apiPaths.missionId, { id }), data).pipe(
      switchMap((res) =>
        this.dbService.getRow(appDb.cacheKeys, 'userMissionKey').pipe(
          switchMap((cache) => {
            return this.getMissions(cache.hash).pipe(map(() => res));
          })
        )
      ),
      switchMap((res) =>
        this.cacheKeyService
          .checkAndUpdateTables('userMissionKey')
          .pipe(map(() => res))
      ),
      catchError((err) => throwError(() => err))
    );
  }
  delete(id) {
    return this.http.delete(generateURL(apiPaths.missionId, { id })).pipe(
      switchMap((res) =>
        this.dbService.getRow(appDb.cacheKeys, 'userMissionKey').pipe(
          switchMap((cache) => {
            return this.getMissions(cache.hash).pipe(map(() => res));
          })
        )
      ),
      switchMap((res) =>
        this.cacheKeyService
          .checkAndUpdateTables('userMissionKey')
          .pipe(map(() => res))
      ),
      catchError((err) => throwError(() => err))
    );
  }
  bulk(missions) {
    return this.http.post(
      generateURL(apiPaths.missionBulk),
      missions.map((item) => ({
        ...item,
        isDeleted: item.isDeleted ? true : false,
      }))
    );
  }
}
